import { useAuth0 } from "@auth0/auth0-react";
import { Button, Modal } from "antd";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { editUser } from "../../utils/users";

const EditUserModal = ({ modalOpen, onClose, user, onSuccess }) => {
  const { getIdTokenClaims } = useAuth0();
  const { enqueueSnackbar } = useSnackbar();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [alternateEmail, setAlternateEmail] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && modalOpen) {
      setName(user.name || "");
      setPhone(
        user.company_phone !== null && user.company_phone !== undefined
          ? String(user.company_phone)
          : ""
      );
      setAlternateEmail(user.alternate_email || "");
    }
  }, [user, modalOpen]);

  const handleClose = () => {
    onClose();
  };

  const handleSave = async () => {
    if (!name.trim()) {
      enqueueSnackbar("User name is required", { variant: "error" });
      return;
    }

    if (alternateEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const emails = alternateEmail
        .split(/[,;\s]+/)
        .map((e) => e.trim())
        .filter(Boolean);
      for (const em of emails) {
        if (!emailRegex.test(em)) {
          enqueueSnackbar(`Invalid alternate email address: "${em}"`, {
            variant: "error",
          });
          return;
        }
      }
    }

    setSaving(true);
    try {
      const token = await getIdTokenClaims();
      const res = await editUser(token.__raw, user.id, {
        name: name.trim(),
        phone: phone.trim(),
        alternate_email: alternateEmail.trim(),
      });

      if (res.data.success) {
        enqueueSnackbar(res.data.message || "User updated successfully", {
          variant: "success",
        });
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } else {
        enqueueSnackbar(res.data.message || "Failed to update user", {
          variant: "error",
        });
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar(
        err.response?.data?.message || "Something went wrong. Please try again.",
        { variant: "error" }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={[
        <div key="title" className={"text-2xl"}>
          Edit User
        </div>,
      ]}
      centered
      open={modalOpen}
      footer={[
        <div key="footer" className={"flex items-center justify-end gap-3"}>
          <Button
            key="cancel"
            type="secondary"
            className={"px-2 text-sm"}
            onClick={handleClose}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            key="submit"
            type="primary"
            className={"px-2 text-sm"}
            onClick={handleSave}
            loading={saving}
          >
            Save
          </Button>
        </div>,
      ]}
      onCancel={handleClose}
    >
      <div className={"py-6 text-sm border-b border-gray-300 w-full"}>
        <div className={"w-full mb-4"}>
          <p className={"font-medium text-sm mb-2"}>Email</p>
          <input
            className={
              "w-full bg-gray-100 rounded-lg border-gray-200 border p-2 text-gray-500 cursor-not-allowed"
            }
            type={"text"}
            value={user?.email || ""}
            disabled
          />
        </div>
        <div className={"w-full mb-4"}>
          <p className={"font-medium text-sm mb-2"}>
            Alternate Email{" "}
            <span className="text-gray-400 font-normal">
              (always CC'd on outgoing emails)
            </span>
          </p>
          <input
            className={
              "w-full bg-gray-50 rounded-lg border-gray-200 border p-2 focus:bg-white focus:outline-none focus:border-blue-500"
            }
            type={"text"}
            placeholder={"e.g. billing@client.com (comma-separated for multiple)"}
            value={alternateEmail}
            onChange={(e) => setAlternateEmail(e.target.value)}
          />
          <p className={"text-xs text-gray-400 mt-1"}>
            Outgoing emails (backups, billing, notifications) sent to this user will automatically CC this address.
          </p>
        </div>
        <div className={"w-full mb-4"}>
          <p className={"font-medium text-sm mb-2"}>
            User Name <span className={"text-red-500"}>*</span>
          </p>
          <input
            className={
              "w-full bg-gray-50 rounded-lg border-gray-200 border p-2 focus:bg-white focus:outline-none focus:border-blue-500"
            }
            type={"text"}
            placeholder={"Enter user full Name"}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className={"w-full mb-2"}>
          <p className={"font-medium text-sm mb-2"}>Phone Number</p>
          <input
            className={
              "w-full bg-gray-50 rounded-lg border-gray-200 border p-2 focus:bg-white focus:outline-none focus:border-blue-500"
            }
            type={"text"}
            placeholder={"Enter phone number"}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
};

export default EditUserModal;
